import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import styles from './CreatePatientPopup.module.css';
import PopupBase from "../base/PopupBase";
import InputField from "../../components/inputfield/InputField";
import RadioSelector from '../../components/radio-selector/RadioSelector';
import SectionPanel from '../../components/section-panel/SectionPanel';
import PatientConfig from "../../consts/PatientConfig";
import { DataProviderInstance } from '../../api/DataProvider';
import CustomButton from "../../components/buttons/CustomButton";
import { FormControl, InputLabel, Select, MenuItem, Switch, CircularProgress, Alert, InputAdornment, IconButton } from '@mui/material';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import FamilyRestroomOutlinedIcon from '@mui/icons-material/FamilyRestroomOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { ValidateCPF, GetCurrentAge } from '../../utils/Utils';
import { PERSON_CREATED_EVENT } from '../../components/guided-tour/DemoCleanupNudge';
import PatientInsuranceSummaryPanel from '../../components/insurance/PatientInsuranceSummaryPanel';
import PatientInsuranceFields, {
    buildInsurancePayload,
    buildInsuranceProviderOptions,
    isInsuranceFormFilled,
    toInsuranceFormState,
    validateInsuranceForm
} from '../../components/insurance/PatientInsuranceFields';

const switchSx = {
    '& .MuiSwitch-switchBase.Mui-checked': { color: '#5A80FB' },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5A80FB' }
};

const selectSx = {
    fontSize: '13px',
    '& .MuiOutlinedInput-notchedOutline': { borderWidth: '1px' }
};

function CreatePatientPopup(props) {

    const
        {
            CompanyConfig,
            GetCEPInfo,
            SetPatient, UpdatePatient, patients,
            GetMarketingChannels, GetMarketingCampaignsByChannel,
            FindLeadByPhone,
            SetPatientInsurance
        } = DataProviderInstance();

    const navigate = useNavigate();

    const { Data, SuggestedName, Visibility, OnChange, OnHide } = props;

    const inputFieldCPFRef = useRef(null);
    const inputFieldNameRef = useRef(null);
    const inputFieldBirthdayRef = useRef(null);
    const genderRef = useRef(null);
    const inputFieldCountryCodeRef = useRef(null);
    const inputFieldPhoneRef = useRef(null);
    const inputFieldEmailRef = useRef(null);
    const inputFieldCEPRef = useRef(null);
    const inputFieldAddressRef = useRef(null);
    const inputFieldNumberRef = useRef(null);
    const inputFieldCompRef = useRef(null);
    const inputFieldBairroRef = useRef(null);
    const inputFieldCidadeRef = useRef(null);
    const inputFieldEstadoRef = useRef(null);
    const inputFieldFinancialNameRef = useRef(null);
    const inputFieldFinancialDocumentRef = useRef(null);
    const inputFieldFinancialEmailRef = useRef(null);
    const inputFieldFinancialPhoneRef = useRef(null);
    const inputFieldFinancialRelationshipRef = useRef(null);

    const [currentData, setCurrendData] = useState(Data);
    const [hasFinancialResponsible, setHasFinancialResponsible] = useState(
        !!(Data?.financial_responsible_name)
    );
    const [isPatientMinor, setIsPatientMinor] = useState(() => IsMinorFromISODate(Data?.birthday));
    const [currentStatus, setCurrentStatus] = useState(Data?.status != null ? Data.status : 1);
    const [patientFound, setPatientFound] = useState(null);
    
    // Estados para os campos de origem do marketing
    const [marketingSources, setMarketingSources] = useState([]);
    const [marketingCampaigns, setMarketingCampaigns] = useState([]);
    const [selectedSourceId, setSelectedSourceId] = useState(Data?.origin_channel_id || "");
    const [selectedCampaignId, setSelectedCampaignId] = useState(Data?.origin_campaign_id || "");
    const [loadingSources, setLoadingSources] = useState(false);
    const [loadingCampaigns, setLoadingCampaigns] = useState(false);

    // Lead com o mesmo telefone (só no cadastro novo): origem pré-preenchida e vínculo ao salvar
    const [foundLeadId, setFoundLeadId] = useState(null);
    const [leadOriginFilled, setLeadOriginFilled] = useState(false);
    const lastLeadLookupPhoneRef = useRef("");

    // Convênio no cadastro novo (opcional, recolhido): a carteirinha é criada logo depois da pessoa
    const insuranceProviders = CompanyConfig?.InsuranceProviders;
    const showInsurance = Data?.id == null && (insuranceProviders || []).length > 0;
    const insuranceOptions = useMemo(() => buildInsuranceProviderOptions(insuranceProviders), [insuranceProviders]);
    const [insuranceOpen, setInsuranceOpen] = useState(false);
    const [insuranceForm, setInsuranceForm] = useState(() => toInsuranceFormState());
    const [insuranceErrors, setInsuranceErrors] = useState({});
    // Pessoa salva e carteirinha não: guarda a resposta do cadastro até a pessoa fechar o aviso
    const [insuranceFailedResponse, setInsuranceFailedResponse] = useState(null);
    const wantsInsurance = showInsurance && insuranceOpen && isInsuranceFormFilled(insuranceForm);

    // Estados de loading individuais para cada botão
    const [loadingStates, setLoadingStates] = useState({
        save: false,
        cancel: false,
        searchCEP: false
    });

    // Função para controlar loading individual dos botões
    const setButtonLoading = useCallback((buttonType, isLoading) => {
        setLoadingStates(prev => ({
            ...prev,
            [buttonType]: isLoading
        }));
    }, []);

    useEffect(() => {
        // Carregar canais de marketing
        setLoadingSources(true);
        GetMarketingChannels((response) => {
            if (response.status === 'success') {
                setMarketingSources(response.data || []);
            }
            setLoadingSources(false);
        });
        
        // Se já tiver um canal selecionado, carregar as campanhas
        if (Data?.origin_channel_id) {
            setSelectedSourceId(Data.origin_channel_id);
            loadCampaignsBySource(Data.origin_channel_id);
        }
        
        return () => {
            Dispose();
        };
    }, []);
    
    // Função para carregar as campanhas de um canal específico
    const loadCampaignsBySource = (sourceId, onLoaded) => {
        if (!sourceId) {
            setMarketingCampaigns([]);
            return;
        }

        setLoadingCampaigns(true);
        GetMarketingCampaignsByChannel(sourceId, (response) => {
            if (response.status === 'success') {
                setMarketingCampaigns(response.data || []);
            }
            setLoadingCampaigns(false);
            if (onLoaded) onLoaded();
        });
    };
    
    // Handler para quando o canal de marketing é alterado
    const handleSourceChange = (sourceId) => {
        setSelectedSourceId(sourceId);
        setSelectedCampaignId(""); // Resetar a campanha selecionada
        loadCampaignsBySource(sourceId);
    };

    // Cadastro novo: telefone de um lead ainda não convertido preenche a origem
    // vazia e guarda o lead para o vínculo ao salvar. Falha fica em silêncio.
    function OnPhoneBlurHandler() {
        if (Data?.id || Data?.leadId) return;

        // O campo pode devolver número (DDI e telefone numéricos): sempre texto antes do replace
        const phone = String(inputFieldPhoneRef.current?.GetValue() ?? "").replace(/[^0-9]/g, '');
        if (phone.length < 10 || phone === lastLeadLookupPhoneRef.current) return;
        lastLeadLookupPhoneRef.current = phone;

        const countryCode = String(inputFieldCountryCodeRef.current?.GetValue() ?? "").replace(/[^0-9]/g, '');

        FindLeadByPhone(countryCode, phone, (response) => {
            const lead = response?.status === 'success' ? response.data : null;

            setFoundLeadId(lead?.id || null);
            setLeadOriginFilled(false);
            if (!lead || !lead.sourceId) return;

            const sourceIsEmpty = !selectedSourceId;
            const sameSource = sourceIsEmpty || String(selectedSourceId) === String(lead.sourceId);
            const fillCampaign = sameSource && !selectedCampaignId && !!lead.campaignId;

            if (!sourceIsEmpty && !fillCampaign) return;

            if (sourceIsEmpty) {
                setSelectedSourceId(lead.sourceId);
            }
            loadCampaignsBySource(lead.sourceId, () => {
                if (fillCampaign) setSelectedCampaignId(lead.campaignId);
            });
            setLeadOriginFilled(true);
        });
    }

    function Dispose() {
        setPatientFound(null);
        //setCurrentStatus(1);
    }

    function ValidateForm() {

        if (inputFieldCPFRef.current.GetValue() != "") {
            if (!ValidateCPF(inputFieldCPFRef.current.GetValue())) {
                inputFieldCPFRef.current.SetError("CPF inválido");
                return false;
            }
        }

        if (inputFieldNameRef.current.Validate() == false) {
            inputFieldNameRef.current.SetError("Preencha o nome do paciente");
            return false;
        }

        // Sexo biológico não é mais obrigatório, então removemos a validação

        if (inputFieldCountryCodeRef.current.Validate() == false) {
            inputFieldCountryCodeRef.current.SetError("Preencha o código do país");
            return false;
        }

        if (inputFieldPhoneRef.current.Validate() == false) {
            inputFieldPhoneRef.current.SetError("Preencha o telefone do paciente");
            return false;
        }

        // Cliente já existente deixa o campo travado; só valida o que a pessoa consegue preencher
        if (hasFinancialResponsible && patientFound == null && inputFieldFinancialNameRef.current?.Validate() == false) {
            inputFieldFinancialNameRef.current.SetError("Preencha o nome do responsável");
            return false;
        }

        if (wantsInsurance) {
            const errors = validateInsuranceForm(insuranceForm);
            setInsuranceErrors(errors);
            if (Object.keys(errors).length > 0) return false;
        }

        return true;
    }

    function OnConfirmClickHandler() {
        if (ValidateForm()) {
            setButtonLoading('save', true);
            
            let id = (Data != null && Data.id != null) ? Data.id : null;
            let birthday = inputFieldBirthdayRef.current.GetValue();

            if (birthday) {
                const [day, month, year] = birthday.split('/');
                birthday = `${year}-${month}-${day}`;
            }

            let data = CreatePatienteData(
                id,
                currentStatus,
                inputFieldCPFRef.current.GetValue(),
                inputFieldNameRef.current.GetValue(),
                birthday,
                genderRef.current.GetValue(),
                inputFieldCountryCodeRef.current.GetValue(),
                inputFieldPhoneRef.current.GetValue(),
                inputFieldEmailRef.current.GetValue(),
                inputFieldCEPRef.current.GetValue(),
                inputFieldAddressRef.current.GetValue(),
                inputFieldNumberRef.current.GetValue(),
                inputFieldCompRef.current.GetValue(),
                inputFieldBairroRef.current.GetValue(),
                inputFieldCidadeRef.current.GetValue(),
                inputFieldEstadoRef.current.GetValue(),
                selectedSourceId,
                selectedCampaignId,
                hasFinancialResponsible ? inputFieldFinancialNameRef.current?.GetValue() || "" : "",
                hasFinancialResponsible ? (inputFieldFinancialDocumentRef.current?.GetValue() || "").replace(/[^0-9]/g, '') : "",
                hasFinancialResponsible ? inputFieldFinancialEmailRef.current?.GetValue() || "" : "",
                hasFinancialResponsible ? (inputFieldFinancialPhoneRef.current?.GetValue() || "").replace(/[^0-9]/g, '') : "",
                hasFinancialResponsible ? inputFieldFinancialRelationshipRef.current?.GetValue() || "" : ""
            );

        }
    }

    function GetFullAdressByCEP(value) {
        // Ativa o estado de carregamento
        setButtonLoading('searchCEP', true);

        GetCEPInfo(value, (result) => {
            // Preenche os campos com os dados retornados
            inputFieldAddressRef.current.SetValue(result.logradouro);
            inputFieldBairroRef.current.SetValue(result.bairro);
            inputFieldCidadeRef.current.SetValue(result.localidade);
            inputFieldEstadoRef.current.SetValue(result.uf);

            // Desativa o estado de carregamento quando a resposta chegar
            setButtonLoading('searchCEP', false);
        });
    }

    function CreatePatienteData(id, status, cpf, name, birthday, gender, countryCode, phone, email, cep, address, number, complement, district, city, state, origin_channel_id, origin_campaign_id, financial_responsible_name, financial_responsible_document, financial_responsible_email, financial_responsible_phone, financial_responsible_relationship) {

        name = name.trim();
        countryCode = countryCode ? countryCode.toString().trim().replace(/[^0-9]/g, '') : "";
        phone = phone ? phone.toString().trim().replace(/[^0-9]/g, '') : "";
        address = address? address.toString().trim() : "";
        number = number? number.toString().trim() : "";
        complement =complement? complement.toString().trim() : "";
        district = district? district.toString().trim() : "";
        city = city ? city.toString().trim() : "";
        state = state? state.toString().trim(): "";

        let patientData = {
            id: id,
            status: status,
            cpf: cpf,
            name: name,
            birthday: birthday,
            gender: gender,
            countryCode: countryCode,
            phone: phone,
            email: email,
            cep: cep,
            address: address,
            number: number,
            complement: complement,
            district: district,
            city: city,
            state: state,
            origin_channel_id: origin_channel_id,
            origin_campaign_id: origin_campaign_id,
            // Conversão de lead: o backend vincula o lead ao paciente criado
            leadId: Data?.leadId || foundLeadId || null,
            financial_responsible_name: financial_responsible_name || "",
            financial_responsible_document: financial_responsible_document || "",
            financial_responsible_email: financial_responsible_email || "",
            financial_responsible_phone: financial_responsible_phone || "",
            financial_responsible_relationship: financial_responsible_relationship || ""
        }

        if (id != null) {
            UpdatePatient(patientData, (response) => {
                setButtonLoading('save', false);
                Hide();
                if (OnChange != null) {
                    OnChange(response);
                }
            })
        }
        else {
            SetPatient(patientData, (response) => {
                const patientId = response?.data?.patientId;
                const created = response && !response.error && patientId != null;

                if (!created || !wantsInsurance) {
                    FinishCreate(response);
                    return;
                }

                SetPatientInsurance(buildInsurancePayload(insuranceForm, patientId), (result) => {
                    if (result?.status === 'SUCCESS' && !result.error) {
                        FinishCreate(response);
                    } else {
                        // A pessoa fica salva; o aviso explica que a carteirinha entra pela ficha
                        setButtonLoading('save', false);
                        setInsuranceFailedResponse(response);
                    }
                });
            });
        }

        return patientData;
    }

    function FinishCreate(response) {
        setButtonLoading('save', false);
        Hide();
        // Pessoa nova salva: com exemplos na conta, o guia pergunta uma vez se quer tirá-los
        if (response && !response.error) window.dispatchEvent(new CustomEvent(PERSON_CREATED_EVENT));
        if (OnChange != null) {
            OnChange(response);
        }
    }

    function OnInsuranceProviderChange(value) {
        // Trocar a operadora zera o plano: ele pertence à operadora
        setInsuranceForm((prev) => ({ ...prev, provider_id: value, plan_id: '' }));
        setInsuranceErrors((prev) => ({ ...prev, provider_id: undefined }));
    }

    function OnInsuranceFieldChange(name, value) {
        setInsuranceForm((prev) => ({ ...prev, [name]: value }));
        setInsuranceErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
    }

    function OnCloseClickHandler() {
        // Pessoa já cadastrada (só a carteirinha falhou): fechar conclui o cadastro
        if (insuranceFailedResponse) {
            FinishCreate(insuranceFailedResponse);
            return;
        }
        setButtonLoading('cancel', true);
        setTimeout(() => {
            setButtonLoading('cancel', false);
            Hide();
        }, 300);
    }

    function Hide() {
        Dispose();
        OnHide();
    }

    function GetTitle() {
        if (Data && Data.id) {
            return "Editar paciente";
        }
        else {
            return "Novo paciente";
        }
    }

    function OnStateChangeHandler(value) {
        setCurrentStatus((value ? 1 : 0));
    }

    function OnGenderSelectHandler(value) {
        // Atualiza o valor do gênero selecionado
        // Não precisamos fazer nada especial aqui, pois o componente RadioSelector
        // já atualiza seu estado interno e o valor pode ser obtido via ref
        
    }


    function OnSearchCEPClickHandler() {
        let cep = inputFieldCEPRef.current.GetValue();

        if (cep.trim() != "") {
            GetFullAdressByCEP(cep);
        }
    }

    function OnCPFChangeHandler(value) {

        const rawValue = value.replace(/[^0-9]/g, '');

        // Limpa qualquer erro anterior
        inputFieldCPFRef.current.ClearError();
        
        // Só valida o CPF se o campo não estiver vazio
        if (rawValue.length > 0) {
            // Verifica se o CPF é válido
            if (!ValidateCPF(rawValue)) {
                inputFieldCPFRef.current.SetError("CPF inválido");
            }

            // Verifica se o CPF já existe
            const existingPatient = patients.find(patient => patient.cpf == rawValue);

            if (existingPatient && existingPatient.id != Data?.id) {
                setPatientFound(existingPatient);
                inputFieldCPFRef.current.SetError("CPF já cadastrado");
                // CPF already exists, handle accordingly
            } else {
                // CPF is unique, continue with the logic
                setPatientFound(null);
            }
        }
        else {
            setPatientFound(null);
        }
    }

    function onFoundPatientClickHandler() {
        navigate(`/patients/${patientFound.id}`);

    }

    // LGPD art. 14: o bloco de responsável também guarda o responsável legal de menores.
    function IsMinorFromISODate(isoBirthday) {
        if (!isoBirthday) return false;
        try {
            const [year, month, day] = isoBirthday.split('-');
            const date = new Date(`${year}-${month}-${day}`);
            if (isNaN(date.getTime())) return false;
            return GetCurrentAge(date) < 18;
        } catch (error) {
            return false;
        }
    }

    function IsMinorFromMaskedDate(maskedBirthday) {
        if (!maskedBirthday) return false;
        const digits = maskedBirthday.replace(/\D/g, '');
        if (digits.length !== 8) return false;
        try {
            const [day, month, year] = maskedBirthday.split('/');
            const date = new Date(`${year}-${month}-${day}`);
            if (isNaN(date.getTime())) return false;
            return GetCurrentAge(date) < 18;
        } catch (error) {
            return false;
        }
    }

    function GetContent() {
        if (insuranceFailedResponse) {
            return (
                <div className={styles.form}>
                    <Alert severity="warning">
                        O cadastro foi salvo, mas não foi possível salvar a carteirinha. Ela pode ser adicionada depois na ficha, na parte Convênio.
                    </Alert>
                </div>
            );
        }

        let currentName = "";
        let currentGender = "99"; // Garante que o valor seja string para corresponder ao formato em PatientConfig
        let currentCountryCode = CompanyConfig?.countryCode || "55";
        let currentPhone = "";
        let currentCPF = "";
        let currentEmail = "";
        let currentBirthday = "";

        let currentCEP = "";
        let currentAddress = "";
        let currentNumber = "";
        let currentComp = "";
        let currentBairro = "";
        let currentCidade = "";
        let currentEstado = "";

        let currentFinancialName = "";
        let currentFinancialDocument = "";
        let currentFinancialEmail = "";
        let currentFinancialPhone = "";
        let currentFinancialRelationship = "";

        if (SuggestedName) {
            currentName = SuggestedName;
        }

        if (Data) {
            currentName = Data.name;

            // Cadastro pré-preenchido (ex.: conversão de lead): sem id, segue sendo criação
            if (Data.countryCode) currentCountryCode = Data.countryCode;
            if (Data.phone) currentPhone = Data.phone;
        }

        if (Data && Data.id) {
            currentCPF = Data.cpf;

            if (Data.birthday) {
                const [year, month, day] = Data.birthday.split('-');
                currentBirthday = `${day}/${month}/${year}`;
            }

            currentGender = Data.gender;
            currentCountryCode = Data.countryCode;
            currentPhone = Data.phone;
            currentEmail = Data.email;

            currentCEP = Data.cep;
            currentAddress = Data.address;
            currentNumber = Data.number;
            currentComp = Data.complement;
            currentBairro = Data.district;
            currentCidade = Data.city;
            currentEstado = Data.state;

            currentFinancialName = Data.financial_responsible_name || "";
            currentFinancialDocument = Data.financial_responsible_document || "";
            currentFinancialEmail = Data.financial_responsible_email || "";
            currentFinancialPhone = Data.financial_responsible_phone || "";
            currentFinancialRelationship = Data.financial_responsible_relationship || "";
        }

        const locked = patientFound != null;
        const addressLocked = loadingStates.searchCEP || locked;

        const statusSwitch = Data?.id != null ? (
            <label className={styles.headerSwitch}>
                <span>{currentStatus == 1 ? 'Cadastro ativo' : 'Cadastro inativo'}</span>
                <Switch
                    checked={currentStatus == 1}
                    onChange={(event) => OnStateChangeHandler(event.target.checked)}
                    size="small"
                    sx={switchSx}
                />
            </label>
        ) : null;

        const responsibleSwitch = (
            <label className={styles.headerSwitch}>
                <span>Possui responsável</span>
                <Switch
                    checked={hasFinancialResponsible}
                    onChange={(event) => setHasFinancialResponsible(event.target.checked)}
                    size="small"
                    sx={switchSx}
                />
            </label>
        );

        const insuranceSwitch = (
            <label className={styles.headerSwitch}>
                <span>Informar agora</span>
                <Switch
                    checked={insuranceOpen}
                    onChange={(event) => setInsuranceOpen(event.target.checked)}
                    size="small"
                    sx={switchSx}
                    disabled={locked}
                />
            </label>
        );

        const cepSearch = (
            <InputAdornment position="end">
                {loadingStates.searchCEP ? (
                    <CircularProgress size={16} />
                ) : (
                    <IconButton
                        size="small"
                        edge="end"
                        className={styles.cepSearch}
                        onClick={OnSearchCEPClickHandler}
                        disabled={locked}
                        aria-label="Buscar endereço pelo CEP"
                        title="Buscar endereço pelo CEP"
                    >
                        <SearchRoundedIcon fontSize="small" />
                    </IconButton>
                )}
            </InputAdornment>
        );

        return (
            <div className={styles.form}>
                <SectionPanel
                    icon={<BadgeOutlinedIcon />}
                    title="Identificação"
                    subtitle="Nome, nascimento e CPF"
                    accent="blue"
                    aside={statusSwitch}
                >
                    <div className={styles.fields}>
                        <div className={`${styles.field} ${styles.c4}`} data-tour="patient-form-name">
                            <InputField
                                Mandatory={true}
                                Value={currentName}
                                Label={"Nome"}
                                ref={inputFieldNameRef}
                                Disabled={locked}
                            />
                        </div>
                        <div className={`${styles.field} ${styles.c2}`}>
                            <InputField
                                Mandatory={false}
                                Value={currentCPF}
                                Label={"CPF"}
                                ref={inputFieldCPFRef}
                                Mask={"999.999.999-99"}
                                OnUpdateValue={OnCPFChangeHandler}
                            />
                        </div>

                        {patientFound && (
                            <Alert
                                severity="warning"
                                className={styles.c6}
                                action={
                                    <CustomButton
                                        label={patientFound.name}
                                        style="primary"
                                        variant="text"
                                        icon={<OpenInNewIcon fontSize="small" />}
                                        onClick={onFoundPatientClickHandler}
                                    />
                                }
                            >
                                Já existe um cadastro com este CPF.
                            </Alert>
                        )}

                        <div className={`${styles.field} ${styles.c2}`}>
                            <InputField
                                Mandatory={false}
                                Value={currentBirthday}
                                Label={"Nascimento"}
                                ref={inputFieldBirthdayRef}
                                Mask={"99/99/9999"}
                                Disabled={locked}
                                OnUpdateValue={(value) => setIsPatientMinor(IsMinorFromMaskedDate(value))}
                            />
                        </div>
                        <div className={`${styles.field} ${styles.c4}`}>
                            <RadioSelector
                                Label={"Sexo biológico"}
                                Mandatory={false}
                                Options={PatientConfig.Genders}
                                InitialSelection={currentGender}
                                OnSelect={OnGenderSelectHandler}
                                ref={genderRef}
                                Disabled={locked}
                            />
                        </div>
                    </div>
                </SectionPanel>

                <SectionPanel
                    icon={<ContactPhoneOutlinedIcon />}
                    title="Contato"
                    subtitle="Telefone e e-mail"
                    accent="teal"
                >
                    <div className={styles.fields}>
                        <div className={`${styles.field} ${styles.c1}`} data-tour="patient-form-ddi">
                            <InputField
                                Mandatory={true}
                                Value={currentCountryCode}
                                Label={"DDI"}
                                ref={inputFieldCountryCodeRef}
                                Mask={"999"}
                                MaskChar={null}
                                Disabled={locked}
                            />
                        </div>
                        <div className={`${styles.field} ${styles.c2}`} data-tour="patient-form-phone">
                            <InputField
                                Mandatory={true}
                                Value={currentPhone}
                                Label={"Telefone"}
                                ref={inputFieldPhoneRef}
                                Mask={"(99) 99999-9999"}
                                Disabled={locked}
                                onBlur={OnPhoneBlurHandler}
                            />
                        </div>
                        <div className={`${styles.field} ${styles.c3}`}>
                            <InputField
                                Mandatory={false}
                                Value={currentEmail}
                                Label={"E-mail"}
                                ref={inputFieldEmailRef}
                                Disabled={locked}
                            />
                        </div>
                    </div>
                </SectionPanel>

                <SectionPanel
                    icon={<PlaceOutlinedIcon />}
                    title="Endereço"
                    subtitle="Digite o CEP e busque para preencher o resto"
                    accent="amber"
                >
                    <div className={styles.fields}>
                        <div className={`${styles.field} ${styles.c2}`}>
                            <InputField
                                Mandatory={false}
                                Value={currentCEP}
                                Label={"CEP"}
                                ref={inputFieldCEPRef}
                                Mask={"99999-999"}
                                Disabled={locked}
                                endAdornment={cepSearch}
                            />
                        </div>
                        <div className={`${styles.field} ${styles.c4}`}>
                            <InputField
                                Mandatory={false}
                                Value={currentAddress}
                                Label={"Endereço"}
                                ref={inputFieldAddressRef}
                                Disabled={addressLocked}
                            />
                        </div>
                        <div className={`${styles.field} ${styles.c1}`}>
                            <InputField
                                Mandatory={false}
                                Value={currentNumber}
                                Label={"Número"}
                                ref={inputFieldNumberRef}
                                Disabled={addressLocked}
                            />
                        </div>
                        <div className={`${styles.field} ${styles.c2}`}>
                            <InputField
                                Mandatory={false}
                                Value={currentComp}
                                Label={"Complemento"}
                                ref={inputFieldCompRef}
                                Disabled={addressLocked}
                            />
                        </div>
                        <div className={`${styles.field} ${styles.c3}`}>
                            <InputField
                                Mandatory={false}
                                Value={currentBairro}
                                Label={"Bairro"}
                                ref={inputFieldBairroRef}
                                Disabled={addressLocked}
                            />
                        </div>
                        <div className={`${styles.field} ${styles.c4}`}>
                            <InputField
                                Mandatory={false}
                                Value={currentCidade}
                                Label={"Cidade"}
                                ref={inputFieldCidadeRef}
                                Disabled={addressLocked}
                            />
                        </div>
                        <div className={`${styles.field} ${styles.c2}`}>
                            <InputField
                                Mandatory={false}
                                Value={currentEstado}
                                Label={"Estado"}
                                ref={inputFieldEstadoRef}
                                Disabled={addressLocked}
                            />
                        </div>
                    </div>
                </SectionPanel>

                <SectionPanel
                    icon={<FamilyRestroomOutlinedIcon />}
                    title="Responsável"
                    subtitle="Financeiro e legal. Obrigatório para menores de 18 anos."
                    accent="purple"
                    aside={responsibleSwitch}
                >
                    {isPatientMinor && !hasFinancialResponsible && (
                        <Alert severity="warning">
                            Paciente menor de idade sem responsável cadastrado. Informe o responsável legal, que deve dar o consentimento para o tratamento dos dados do paciente (LGPD, art. 14).
                        </Alert>
                    )}
                    {hasFinancialResponsible && (
                        <div className={styles.fields}>
                            <div className={`${styles.field} ${styles.c4}`}>
                                <InputField
                                    Mandatory={true}
                                    Value={currentFinancialName}
                                    Label={"Nome / Razão social"}
                                    ref={inputFieldFinancialNameRef}
                                    Disabled={locked}
                                />
                            </div>
                            <div className={`${styles.field} ${styles.c2}`}>
                                <InputField
                                    Mandatory={false}
                                    Value={currentFinancialDocument}
                                    Label={"CPF / CNPJ"}
                                    ref={inputFieldFinancialDocumentRef}
                                    Disabled={locked}
                                />
                            </div>
                            <div className={`${styles.field} ${styles.c2}`}>
                                <InputField
                                    Mandatory={false}
                                    Value={currentFinancialRelationship}
                                    Label={"Parentesco / relação"}
                                    ref={inputFieldFinancialRelationshipRef}
                                    Disabled={locked}
                                />
                            </div>
                            <div className={`${styles.field} ${styles.c2}`}>
                                <InputField
                                    Mandatory={false}
                                    Value={currentFinancialPhone}
                                    Label={"Telefone"}
                                    ref={inputFieldFinancialPhoneRef}
                                    Mask={"(99) 99999-9999"}
                                    Disabled={locked}
                                />
                            </div>
                            <div className={`${styles.field} ${styles.c2}`}>
                                <InputField
                                    Mandatory={false}
                                    Value={currentFinancialEmail}
                                    Label={"E-mail"}
                                    ref={inputFieldFinancialEmailRef}
                                    Disabled={locked}
                                />
                            </div>
                        </div>
                    )}
                </SectionPanel>

                {/* Convênio no cadastro novo: opcional e recolhido; a carteirinha é criada depois da pessoa */}
                {showInsurance && (
                    <SectionPanel
                        icon={<BadgeOutlinedIcon />}
                        title="Convênio"
                        subtitle="Opcional. Operadora, plano e carteirinha"
                        accent="teal"
                        aside={insuranceSwitch}
                    >
                        {insuranceOpen && (
                            <PatientInsuranceFields
                                form={insuranceForm}
                                errors={insuranceErrors}
                                options={insuranceOptions}
                                disabled={locked || loadingStates.save}
                                idPrefix="new-person-insurance"
                                onProviderChange={OnInsuranceProviderChange}
                                onFieldChange={OnInsuranceFieldChange}
                            />
                        )}
                    </SectionPanel>
                )}

                {/* Convênio na edição: só leitura; as carteirinhas são gerenciadas na ficha */}
                {Data?.id != null && (
                    <PatientInsuranceSummaryPanel patientId={Data.id} onNavigate={Hide} />
                )}

                <SectionPanel
                    icon={<CampaignOutlinedIcon />}
                    title="Origem"
                    subtitle="Como chegou até você"
                    accent="green"
                >
                    <div className={styles.fields}>
                        {leadOriginFilled && (
                            <p className={styles.leadFoundHint}>Lead encontrado: origem preenchida</p>
                        )}
                        <div className={`${styles.field} ${styles.c3}`}>
                            <FormControl
                                fullWidth
                                disabled={locked || loadingSources}
                                size="small"
                            >
                                <InputLabel style={{ fontSize: '13px' }}>Canal de marketing</InputLabel>
                                <Select
                                    label="Canal de marketing"
                                    value={selectedSourceId}
                                    onChange={(event) => handleSourceChange(event.target.value)}
                                    startAdornment={loadingSources ? <CircularProgress size={16} style={{ marginRight: '6px' }} /> : null}
                                    sx={selectSx}
                                >
                                    <MenuItem value="" style={{ fontSize: '13px' }}>Selecione um canal</MenuItem>
                                    {marketingSources.map((source) => (
                                        <MenuItem key={source.id} value={source.id} style={{ fontSize: '13px' }}>{source.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                        {selectedSourceId && (
                            <div className={`${styles.field} ${styles.c3}`}>
                                <FormControl
                                    fullWidth
                                    disabled={locked || loadingCampaigns}
                                    size="small"
                                >
                                    <InputLabel style={{ fontSize: '13px' }}>Campanha de marketing</InputLabel>
                                    <Select
                                        label="Campanha de marketing"
                                        value={selectedCampaignId}
                                        onChange={(event) => setSelectedCampaignId(event.target.value)}
                                        startAdornment={loadingCampaigns ? <CircularProgress size={16} style={{ marginRight: '6px' }} /> : null}
                                        sx={selectSx}
                                    >
                                        <MenuItem value="" style={{ fontSize: '13px' }}>Selecione uma campanha</MenuItem>
                                        {marketingCampaigns.map((campaign) => (
                                            <MenuItem key={campaign.id} value={campaign.id} style={{ fontSize: '13px' }}>{campaign.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                        )}
                    </div>
                </SectionPanel>
            </div>
        );
    }

    function GetPositiveButton() {
        const callToAction = Data && Data.id ? "Salvar" : "Adicionar";

        return (
            <CustomButton
                style="main"
                variant="contained"
                label={loadingStates.save ? <CircularProgress size={16} color="inherit" /> : callToAction}
                onClick={OnConfirmClickHandler}
                enabled={!patientFound && !loadingStates.save}
                data-tour={Data && Data.id ? undefined : 'patient-form-add'}
            />
        )
    }

    function GetFooter() {
        if (insuranceFailedResponse) {
            return (
                <div className={styles.footer}>
                    <CustomButton
                        style="main"
                        variant="contained"
                        label="Concluir"
                        onClick={() => FinishCreate(insuranceFailedResponse)}
                    />
                </div>
            );
        }

        return (
            <div className={styles.footer}>
                <CustomButton
                    style="primary"
                    variant="text"
                    label={loadingStates.cancel ? <CircularProgress size={16} color="inherit" /> : "Cancelar"}
                    onClick={OnCloseClickHandler}
                    enabled={!loadingStates.cancel}
                />
                {GetPositiveButton()}
            </div>
        )
    }

    function GetRender() {
        if (Visibility)
            return <PopupBase Title={GetTitle()} Content={GetContent()} Footer={GetFooter()} OnClose={OnCloseClickHandler} maxWidth="md" />
        else
            return null;
    }

    return (GetRender());
}

export default CreatePatientPopup;
